/* eslint-disable */
import { useEffect, useRef } from "react";

/**
 * Standard modal a11y wiring:
 *  - focuses the first focusable element on mount,
 *  - traps Tab + Shift+Tab inside the modal,
 *  - closes on Escape,
 *  - restores focus to whatever was focused before the modal opened.
 *
 * Usage:
 *   const modalRef = useModalA11y({ isOpen: true, onClose });
 *   return <div ref={modalRef} role="dialog" aria-modal="true">…</div>;
 */
export default function useModalA11y({ isOpen, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocused = document.activeElement;
    const node = ref.current;

    const focusables = () => {
      if (!node) return [];
      return Array.from(
        node.querySelectorAll(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );
    };

    // Initial focus — prefer the first input/textarea, fall back to first
    // focusable, fall back to the container itself.
    const candidates = focusables();
    const firstInput = candidates.find((el) =>
      ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName)
    );
    (firstInput || candidates[0] || node)?.focus?.();

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose && onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const els = focusables();
      if (els.length === 0) {
        e.preventDefault();
        return;
      }
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (previouslyFocused && previouslyFocused.focus) {
        try {
          previouslyFocused.focus();
        } catch (_) {}
      }
    };
  }, [isOpen, onClose]);

  return ref;
}
